import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngxs/store';
import { IIngredientWasteForAmend } from 'src/app/inventory/interfaces/ingredient-for-amend.interface';
import { ActivityTypesEnum } from 'src/app/shared/interfaces/ActivityTypesEnum';
import { DrugScheduleEnum } from 'src/app/shared/interfaces/DrugScheduleEnum ';
import { FieldType } from 'src/app/shared/interfaces/field-type.enum';
import { GenericTableColumn } from 'src/app/shared/interfaces/genetic-table-column';
import { RowType } from 'src/app/shared/interfaces/raw-type.enum';
import { ITransaction } from 'src/app/shared/interfaces/transaction.interface';
import { BackCountService } from 'src/app/shared/services/back-count.service';
import { PharmacyState } from 'src/app/shared/states/pharmacy/pharmacy.state';

@Component({
  selector: 'app-compound-amend-table',
  templateUrl: './compound-amend-table.component.html',
  styleUrls: ['./compound-amend-table.component.scss', '.././amend-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [BackCountService]
})
export class CompoundAmendTableComponent implements OnInit {

  @Input() compound: ITransaction;
  @Input() compoundCurrentInventory: number;
  @Input() ingredientsWasteData: IIngredientWasteForAmend[];
  @Input() form: FormGroup;

  ingredients: IIngredientWasteForAmend[] = [];
  waste: IIngredientWasteForAmend[] = [];

  compoundColumns: GenericTableColumn<IIngredientWasteForAmend>[] = [];
  ingredientColumns: GenericTableColumn<IIngredientWasteForAmend>[] = [];

  private backCountEnabledForCompound: boolean;

  /**
   *
   */
  constructor(private store: Store, private fb: FormBuilder, private backCountService: BackCountService) {
    this.backCountEnabledForCompound = this.backCountService.enabledForActivityAndSchedule(ActivityTypesEnum.Amendment, DrugScheduleEnum.Compound);

    this.compoundColumns.push(new GenericTableColumn<IIngredientWasteForAmend>({ header: 'Drug', field: 'friendlyName',  RowType: RowType.Double,
      doubleRowField: {
        headingField: 'friendlyName', headingFieldType: FieldType.Text, 
        secondaryField1: 'packageNdc', secondaryFieldType1: FieldType.Text,
      }, 
      style: {width: '350px'},
      class: '!pl-4'
    }));

    if (this.store.selectSnapshot(PharmacyState.inventoryLocationsEnabled)) {
      this.compoundColumns.push(new GenericTableColumn<IIngredientWasteForAmend>({ header: 'Location', field: 'locationName', sortable: false, 
        style: {width: '180px'},  class: '!pl-4'
        }));
    }
    this.compoundColumns.push(...[new GenericTableColumn<IIngredientWasteForAmend>({ header: 'Starting', field: 'startingQuantity', sortable: false, fieldType: FieldType.Number, style: {width: '90px'},  class: '!pl-4' }),
      new GenericTableColumn<IIngredientWasteForAmend>({ header: 'Amended', field: 'amended', sortable: false, fieldType: FieldType.Number,  style: {width: '90px'},  class: '!pl-4' }), 
      new GenericTableColumn<IIngredientWasteForAmend>({ header: 'On-Hand', field: 'endingQuantity', sortable: false, fieldType: FieldType.Number, style: {width: '105px'},  class: '!pl-4' })]
      );

    //ingredient
    this.ingredientColumns = [...this.compoundColumns];

    const showBackCountHeaderPredicate = () => this.backCountService.isAnyRequiresBackCount();
    const showBackCountTogglePredicate = (rowValue) => this.backCountService.isDrugRequiresBackCount(rowValue.drugId);
    
    this.ingredientColumns.push( new GenericTableColumn<IIngredientWasteForAmend>({
      header: 'BACK-COUNT OK?',
      field: 'isBackCountMatches',
      RowType: RowType.BackCount,
      sortable: false,
      style: {width: '163px'},  class: '!pl-4',
      headerDisplayPredicate: showBackCountHeaderPredicate,
      rowValueDisplayPredicate: showBackCountTogglePredicate
    }));

    if(this.backCountEnabledForCompound) {
      this.compoundColumns.push( new GenericTableColumn<IIngredientWasteForAmend>({
        header: 'BACK-COUNT OK?',
        field: 'isBackCountMatches',
        RowType: RowType.BackCount,
        sortable: false,
        style: {width: '163px'},  class: '!pl-4'
      }));
    }
  }


  ngOnInit(): void {
   this.populateCompound();

    const ingredientsFormArray = this.form.get('ingredients') as FormArray;
    const ingredients = this.ingredientsWasteData?.filter(x => x.activityType === ActivityTypesEnum.Mixed);
    this.populateForm(ingredientsFormArray, ingredients);

    const wasteFormArray = this.form.get('waste') as FormArray;
    const waste = this.ingredientsWasteData?.filter(x => x.activityType === ActivityTypesEnum.WastedDestroyed);
    this.populateForm(wasteFormArray, waste);
    
    this.adjustWasteStartingEndingQty();
  }

  private adjustWasteStartingEndingQty() {
    const ingredientsFormArrayVal = (this.form.get('ingredients') as FormArray).getRawValue();

    const wasteFormArray = (this.form.get('waste') as FormArray);

    wasteFormArray?.controls?.forEach(waste => {
      const wasteVal = waste.getRawValue();
      const ingredient  = ingredientsFormArrayVal.find(x => x.locationName === wasteVal.locationName && x.packageNdc === wasteVal.packageNdc);
      if (ingredient) {
        waste.get('startingQuantity').patchValue(ingredient.endingQuantity);
        waste.get('endingQuantity').patchValue(+ingredient.endingQuantity +  +waste.getRawValue().amended);
      }
    });
  }

  private populateForm(formArray: FormArray, data: IIngredientWasteForAmend[]): void {
    formArray.clear();
    data?.map(ingredient => {
    const backCountEnabledForTheActivity = this.backCountService.enabledForActivityAndSchedule(ActivityTypesEnum.Amendment, ingredient.drug.schedule, ingredient.drug.id);
    const group = this.fb.group({
      id: ingredient.id,
      friendlyName: ingredient.drug.friendlyName,
      drugId: ingredient.drug.id,
      packageNdc: ingredient.drug.packageNdc,
      locationName: ingredient?.locationName,
      startingQuantity: ingredient?.onHand,
      amended: Math.abs(ingredient?.addedQty),
      endingQuantity: +ingredient?.onHand + Math.abs(+ingredient?.addedQty),
      isBackCountMatches: [null, backCountEnabledForTheActivity ? Validators.required : null],
    });
      formArray.push(group);
    });
  }

  private populateCompound(): void {
    const compoundControl = this.form.get('compound') as FormArray;
    compoundControl.clear();
    const group = this.fb.group({
      id: this.compound.id,
      friendlyName: this.compound.drug.friendlyName,
      packageNdc: this.compound.drug.packageNdc,
      locationName: this.compound?.locationName,
      startingQuantity: this.compoundCurrentInventory,
      amended: 0 - Math.abs(this.compound?.addedQuantity),
      endingQuantity: +this.compoundCurrentInventory - +this.compound?.addedQuantity,
      isBackCountMatches: [null, this.backCountEnabledForCompound ? Validators.required : null],
    });
    compoundControl.push(group);
  
  }
  

}
